import { useMemo } from 'react'
import { ScoopText, ScoopAccordion, ScoopTable } from '../../../../../../ui'
import { MarkdownSection } from '../MarkdownSection/MarkdownSection'
import { BlufStatement } from '../../../BlufStatement/BlufStatement'
import { StatStrip } from '../../../StatStrip/StatStrip'
import { SeverityTag } from '../../../SeverityTag/SeverityTag'
import { SeverityDistributionBar } from '../../../SeverityDistributionBar/SeverityDistributionBar'
import { severityRank } from '../../../../utils/severity'
import styles from './ExecutiveSummary.module.scss'

const sortedSeverity = (items) => {
    if (!items || items.length === 0) return []
    return [...items].sort((a, b) => severityRank(a.level) - severityRank(b.level))
}

export const ExecutiveSummary = ({
    entitiesNoun = 'Entities',
    bluf,
    severityAssessment = [],
    narrative,
    totalInvestigations,
    // Backend field name is historical: for group reports this is the FLAGGED
    // ENTITY count (DomainIntelligenceAction.buildGroupReportSummary), not a
    // confidence measure — label it truthfully (audit F8).
    highConfidenceCount: flaggedCount,
    entitySummaries = [],
}) => {
    const sorted = useMemo(() => sortedSeverity(severityAssessment), [severityAssessment])

    // Multi-entity mode: severity items have entityId field
    const isMultiEntity = entitySummaries.length >= 2

    // Cross-entity comparison table (only for multi-entity)
    const comparisonColumns = useMemo(() => {
        if (!isMultiEntity) return []
        return [
            { field: 'metric', header: 'Metric', width: 140 },
            ...entitySummaries.map((e) => ({
                field: e.entityId,
                header: e.entityId,
                flex: 1,
                minWidth: 120,
            })),
        ]
    }, [entitySummaries, isMultiEntity])

    const comparisonRows = useMemo(() => {
        if (!isMultiEntity) return []
        const statusRow = { _rowId: 0, metric: 'Status' }
        const evidenceRow = { _rowId: 1, metric: 'Evidence' }
        const findingRow = { _rowId: 2, metric: 'Key Finding' }
        entitySummaries.forEach((e) => {
            const diagParts = (e.diagnosis || '').split(/\s*→\s*/)
            statusRow[e.entityId] = diagParts[0] || '—'
            evidenceRow[e.entityId] =
                `${e.investigationCount || '—'} analyses, ${e.keyFindings?.length || 0} findings`
            const finding = e.keyFindings?.[0] || '—'
            findingRow[e.entityId] = finding.length > 80 ? finding.slice(0, 77) + '...' : finding
        })
        return [statusRow, evidenceRow, findingRow]
    }, [entitySummaries, isMultiEntity])

    const hasAtAGlance = entitySummaries.length > 0 || totalInvestigations > 0 || flaggedCount > 0

    // The operator's first question is "how many need attention" — Flagged is
    // a first-class stat with attention color, not a parenthetical (review D1).
    const statItems = []
    if (entitySummaries.length > 0) {
        statItems.push({
            value: entitySummaries.length,
            label: entitySummaries.length === 1 ? 'Entity Analyzed' : `${entitiesNoun} Analyzed`,
        })
    }
    if (totalInvestigations > 0) {
        statItems.push({ value: totalInvestigations, label: 'Analyses' })
    }
    if (flaggedCount > 0 || entitySummaries.length > 0) {
        statItems.push({
            value: flaggedCount || 0,
            label: 'Flagged',
            tone: flaggedCount > 0 ? 'warning' : undefined,
        })
    }

    return (
        <div className={styles.executiveSummary}>
            {bluf && <BlufStatement>{bluf}</BlufStatement>}

            {hasAtAGlance && statItems.length > 0 && <StatStrip items={statItems} />}

            {/* One glance: the severity split across the whole scanned scope */}
            {entitySummaries.length > 0 && (
                <SeverityDistributionBar
                    entities={entitySummaries}
                    entitiesNoun={entitiesNoun.toLowerCase()}
                />
            )}

            {sorted.length > 0 && (
                <div className={styles.severitySection}>
                    <ScoopText className={styles.sectionTitle}>Severity Assessment</ScoopText>
                    <div className={styles.severityGrid}>
                        {sorted.map((item, idx) => (
                            <div key={idx} className={styles.severityRow}>
                                <ScoopText className={styles.severityIndicator}>
                                    {item.indicator}
                                </ScoopText>
                                <SeverityTag severity={item.level} />
                                <ScoopText className={styles.severityDetail}>
                                    {isMultiEntity && item.entityId
                                        ? `${item.detail} — ${item.entityId}`
                                        : item.detail}
                                </ScoopText>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {isMultiEntity && comparisonRows.length > 0 && (
                <div className={styles.comparisonSection}>
                    <ScoopText className={styles.sectionTitle}>Cross-Entity Comparison</ScoopText>
                    <ScoopTable
                        columns={comparisonColumns}
                        rows={comparisonRows}
                        domLayout="autoHeight"
                        rowHeight={36}
                        headerHeight={40}
                    />
                </div>
            )}

            {narrative && (
                <ScoopAccordion defaultExpanded>
                    <ScoopAccordion.Summary>
                        <ScoopText className={styles.sectionTitle}>Narrative</ScoopText>
                    </ScoopAccordion.Summary>
                    <ScoopAccordion.Details>
                        <MarkdownSection content={narrative} />
                    </ScoopAccordion.Details>
                </ScoopAccordion>
            )}
        </div>
    )
}
